import type { IntlShape } from "@formatjs/intl";
import { Button, DatePicker, Flex, Form, Input, Modal, TimePicker } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { combineDateAndTime } from "../../../common/utils/combineDateAndTime";
import type { TicketStandbyPeriod } from "../../generated-axios-client";

export type StandbyPeriodEditPayload = {
  started_at: string;
  ended_at?: string;
  message?: string;
};

type EditStandbyPeriodModalProps = {
  open: boolean;
  period: TicketStandbyPeriod;
  loading: boolean;
  intl: IntlShape;
  onSubmit: (payload: StandbyPeriodEditPayload) => void;
  onCancel: () => void;
};

type FormValues = {
  startDate: Dayjs | null;
  startTime: Dayjs | null;
  endDate: Dayjs | null;
  endTime: Dayjs | null;
  message: string;
};

export const EditStandbyPeriodModal = ({
  open,
  period,
  loading,
  intl,
  onSubmit,
  onCancel,
}: EditStandbyPeriodModalProps) => {
  const [form] = Form.useForm<FormValues>();

  const originalStart = period.started_at ? dayjs(period.started_at) : null;
  const originalEnd = period.ended_at ? dayjs(period.ended_at) : null;
  // Only closed periods reach this modal today, because the resume row it hangs off is
  // written when the period closes. The guard matters anyway: the API refuses to reopen
  // a closed period, so an open one must never be given a clearable end.
  const isOpenPeriod = originalEnd === null;

  const initialValues: FormValues = {
    startDate: originalStart,
    startTime: originalStart,
    endDate: originalEnd,
    endTime: originalEnd,
    message: period.message ?? "",
  };

  const watchedStartDate = Form.useWatch("startDate", form);
  const watchedStartTime = Form.useWatch("startTime", form);
  const watchedEndDate = Form.useWatch("endDate", form);
  const watchedEndTime = Form.useWatch("endTime", form);

  const pendingStart = combineDateAndTime(watchedStartDate, watchedStartTime);
  const pendingEnd = combineDateAndTime(watchedEndDate, watchedEndTime);
  const endsBeforeStart =
    pendingStart != null && pendingEnd != null && !pendingEnd.isAfter(pendingStart);

  // Both of these are rejected by the API as incorrect_date, whose shared message is
  // worded for check-in ("can't precede your last check-out") and would read as
  // nonsense here. Blocking them in the form keeps that code unreachable from this
  // modal rather than showing the wrong explanation for a real mistake. maxDate alone
  // is not enough: it constrains the date, leaving a later time today selectable.
  const now = dayjs();
  const startsInFuture = pendingStart?.isAfter(now) ?? false;
  const endsInFuture = pendingEnd?.isAfter(now) ?? false;

  const handleSubmit = async () => {
    const values = await form.validateFields();

    const nextStart = combineDateAndTime(values.startDate, values.startTime);
    if (nextStart === null) {
      return;
    }
    const nextEnd = combineDateAndTime(values.endDate, values.endTime);

    onSubmit({
      started_at: nextStart.toISOString(),
      // Omitted rather than sent as null: the API rejects clearing the end of a closed
      // period with cannot_reopen_standby_period.
      ended_at: isOpenPeriod || nextEnd === null ? undefined : nextEnd.toISOString(),
      message: values.message.trim(),
    });
  };

  return (
    <Modal
      open={open}
      title={intl.formatMessage({ id: "standby_period_edit_title" })}
      onCancel={onCancel}
      footer={
        <Flex gap={8} justify="flex-end">
          <Button onClick={onCancel}>{intl.formatMessage({ id: "cancel" })}</Button>
          <Button
            type="primary"
            loading={loading}
            disabled={endsBeforeStart || startsInFuture || endsInFuture}
            onClick={handleSubmit}
          >
            {intl.formatMessage({ id: "confirm" })}
          </Button>
        </Flex>
      }
      destroyOnHidden
    >
      <Flex vertical gap={12} style={{ marginTop: 16 }}>
        <Form form={form} layout="vertical" initialValues={initialValues}>
          <Form.Item
            name="startDate"
            label={intl.formatMessage({ id: "standby_period_edit_start_date" })}
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: "ticket_time_edit_required" }),
              },
            ]}
          >
            <DatePicker style={{ width: "100%" }} maxDate={dayjs()} />
          </Form.Item>
          <Form.Item
            name="startTime"
            label={intl.formatMessage({ id: "standby_period_edit_start_time" })}
            validateStatus={startsInFuture ? "error" : undefined}
            help={
              startsInFuture
                ? intl.formatMessage({ id: "standby_period_edit_in_future" })
                : undefined
            }
            rules={[
              {
                required: true,
                message: intl.formatMessage({ id: "ticket_time_edit_required" }),
              },
            ]}
          >
            <TimePicker format="HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          {!isOpenPeriod && (
            <>
              <Form.Item
                name="endDate"
                label={intl.formatMessage({ id: "standby_period_edit_end_date" })}
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: "ticket_time_edit_required" }),
                  },
                ]}
              >
                <DatePicker style={{ width: "100%" }} maxDate={dayjs()} />
              </Form.Item>
              <Form.Item
                name="endTime"
                label={intl.formatMessage({ id: "standby_period_edit_end_time" })}
                validateStatus={endsBeforeStart || endsInFuture ? "error" : undefined}
                help={
                  endsBeforeStart
                    ? intl.formatMessage({ id: "standby_period_edit_end_before_start" })
                    : endsInFuture
                      ? intl.formatMessage({ id: "standby_period_edit_in_future" })
                      : undefined
                }
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({ id: "ticket_time_edit_required" }),
                  },
                ]}
              >
                <TimePicker format="HH:mm" style={{ width: "100%" }} />
              </Form.Item>
            </>
          )}
          {/* Keeps the default Form.Item bottom margin, unlike the pickers above: the
              showCount counter renders below the TextArea and needs that room, or it
              sits on top of the footer buttons. Matches the reject-work-order modal. */}
          <Form.Item
            name="message"
            label={intl.formatMessage({ id: "standby_period_edit_reason" })}
          >
            <Input.TextArea rows={2} maxLength={240} showCount />
          </Form.Item>
        </Form>
      </Flex>
    </Modal>
  );
};
